import type { OnInit } from '@angular/core'
import { Component, DestroyRef, inject, input, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { TranslatePipe } from '@ngx-translate/core'
import { Subject, catchError, of } from 'rxjs'
import { debounceTime, distinctUntilChanged } from 'rxjs/operators'

import { ArticlesService } from '../../../api/articles-service/articles.service'
import type { MagazineDto } from '../../../api/responses'
import { ArticleList } from '../../../components/article-list'
import { ArticleTypeSelectFilter } from '../../../components/filter-group/components/filters/article-type-select-filter'
import type { DateRange } from '../../../components/filter-group/components/filters/components/components/filter-date-range'
import type { stringMap } from '../../../components/filter-group/components/filters/components/types'
import { DateRangeFilter } from '../../../components/filter-group/components/filters/date-range-filter'
import { SourceSelectFilter } from '../../../components/filter-group/components/filters/source-select-filter'
import { FilterGroup } from '../../../components/filter-group/filter-group'
import { PaginationComponent } from '../../../components/pagination/pagination.component'
import { SearchBarComponent } from '../../../components/search-bar/search-bar.component'
import type {
  ArticleWithNullableRefs,
  PaginationConfig,
} from '../../../types/types'

@Component({
  selector: 'dhp-se-magazine-articles',
  templateUrl: './se-magazine-articles.component.html',
  styleUrls: ['./se-magazine-articles.component.scss'],
  imports: [
    SearchBarComponent,
    ArticleList,
    PaginationComponent,
    TranslatePipe,
    FilterGroup,
    DateRangeFilter,
    SourceSelectFilter,
    ArticleTypeSelectFilter,
  ],
})
export class SEMagazineArticlesComponent implements OnInit {
  private readonly articlesService = inject(ArticlesService)
  private readonly destroyRef = inject(DestroyRef)

  public readonly magazine = input.required<MagazineDto>()

  protected readonly articles = signal(new Set<ArticleWithNullableRefs>())
  protected magazineId = signal<number | null>(null)
  protected readonly loading = signal(true)
  protected readonly error = signal<string | null>(null)
  protected readonly searchTerm = signal('')
  protected readonly totalArticles = signal(0)
  protected rulingDateRangeFilter: DateRange | null = null
  protected sourceFilter: Set<string> | null = null
  protected readonly articleTypesFilter = signal<Set<string> | null>(null)

  private readonly searchTermEvent$ = new Subject<string>()

  protected paginationConfig = signal<PaginationConfig>({
    currentPage: 1,
    totalPages: 0,
    itemsPerPage: 10,
    showItemsPerPage: false,
    onPageChange: this.onPageChange.bind(this),
  })

  ngOnInit(): void {
    // initial load (empty search)
    this.loadArticles()

    this.searchTermEvent$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(searchTerm => {
        // Reset page to 1 on search term change
        this.paginationConfig.update(c => ({ ...c, currentPage: 1 }))

        this.searchTerm.set(searchTerm)
        this.loadArticles()
      })
  }

  onSearchTermChange(term: string): void {
    this.searchTermEvent$.next(term ?? '')
  }

  onPageChange(page: number): void {
    this.paginationConfig.update(config => ({
      ...config,
      currentPage: page,
    }))

    this.loadArticles()
  }

  protected loadArticles(): void {
    this.loading.set(true)
    this.error.set(null)

    const magazine = this.magazine()
    if (!magazine?.id) {
      this.handleError('pages.articles.errors.noMagazine')
      return
    }
    const magazineId = magazine.id
    this.magazineId.set(this.magazine()?.id ?? null)

    this.articlesService
      .getArticlesByMagazineId(
        magazineId,
        this.paginationConfig().itemsPerPage,
        this.paginationConfig().currentPage,
        this.searchTerm(),
        this.rulingDateRangeFilter,
        this.sourceFilter,
        this.articleTypesFilter(),
        {
          property: 'published_at',
          descending: true,
        }
      )
      .pipe(
        catchError(() => {
          this.handleError('pages.articles.errors.failedToLoad')
          return of(null)
        })
      )
      .subscribe(response => {
        if (!response) return

        this.articles.set(
          new Set(
            response.articles.map(article => {
              const { magazine, edition, ...rest } = article
              return {
                ...rest,
                magazine: magazine
                  ? ({
                      ...magazine,
                      title: magazine.title ?? null,
                    } as MagazineDto & { title: string | null })
                  : null,
                edition: edition
                  ? {
                      ...edition,
                      edition_version: edition.edition_version ?? null,
                    }
                  : null,
              }
            })
          )
        )

        this.loading.set(false)
        this.totalArticles.set(response.totalDocs)
        this.paginationConfig.update(config => ({
          ...config,
          totalPages: response.totalPages,
          currentPage: response.page,
        }))
      })
  }

  private handleError(errorKey: string): void {
    this.error.set(errorKey)
    this.loading.set(false)
  }

  protected onDateRangeChange(dateRange: DateRange | null): void {
    this.rulingDateRangeFilter = dateRange

    this.loadArticles()
  }

  protected onSourcesChange(sources: Set<stringMap> | null): void {
    let sourceKeys: Set<string> | null = null
    if (sources !== null) {
      sourceKeys = new Set<string>()

      for (const source of sources) sourceKeys.add(source.key)
    }

    this.sourceFilter = sourceKeys

    this.loadArticles()
  }

  protected onArticleTypesChange(types: Set<stringMap> | null): void {
    let typeKeys: Set<string> | null = null
    if (types !== null) {
      typeKeys = new Set<string>()

      for (const type of types) typeKeys.add(type.key)
    }

    this.articleTypesFilter.set(typeKeys)

    // Reset page to 1, the current page may no longer exist for the new filter
    this.paginationConfig.update(c => ({ ...c, currentPage: 1 }))

    this.loadArticles()
  }

  protected onResetFilters(): void {
    this.rulingDateRangeFilter = null
    this.sourceFilter = null
    this.articleTypesFilter.set(null)

    this.loadArticles()
  }

  protected readonly Array = Array
}
